import type { ChangeState, DBGridOptions, GridOptions, GridRow, GridSidebarConfig } from '../grid.types';
import { isAggregateConfigDefined } from './grid.helpers';

export interface ApplyOptionsFromColschemaContext {
    validateStateMethod: (state: ChangeState) => void;
}

/**
 * Applies grid options configured in the colschema gridoptions column.
 */
export function applyOptionsFromColschema<TType extends GridRow = GridRow>(dbGridOptions: DBGridOptions, options: GridOptions<TType>, context: ApplyOptionsFromColschemaContext): void {
    context.validateStateMethod('add');
    context.validateStateMethod('update');
    context.validateStateMethod('remove');

    if (dbGridOptions.reports != null) options.reports = dbGridOptions.reports;

    setLoadConfigFromDbOptions(dbGridOptions, options);
    setAddConfigFromDbOptions(dbGridOptions, options);
    setRemoveConfigFromDbOptions(dbGridOptions, options);
    setSaveConfigFromDbOptions(dbGridOptions, options);
    setGeneralConfigFromDbOptions(dbGridOptions, options);
    setCssConfigFromDbOptions(dbGridOptions, options);
    setNavigationConfigFromDbOptions(dbGridOptions, options);
    setTooltipConfigFromDbOptions(dbGridOptions, options);
    setAggregatesFromDbOptions(dbGridOptions, options);
    setSelectableFromDbOptions(dbGridOptions, options);
    setSortableFromDbOptions(dbGridOptions, options);
    setFilterableFromDbOptions(dbGridOptions, options);
    setSidebarConfigFromDbOptions(dbGridOptions, options);
    setExcelExportConfigFromDbOptions(dbGridOptions, options);
    setSpecialFuncFromDbOptions(dbGridOptions, options);
}

function setLoadConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.load) {
        if (dbOptions.load?.primaryKey != null) options.dataTask!.loadData!.primaryKey = dbOptions.load!.primaryKey;
        if (dbOptions.load?.rebindOnRead != null) options.dataTask!.loadData!.rebindOnRead = dbOptions.load!.rebindOnRead;
        if (dbOptions.load?.reloadColschemaOnRead != null) options.dataTask!.loadData!.reloadColschemaOnRead = dbOptions.load!.reloadColschemaOnRead;
    }
}

function setAddConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.add) {
        if (dbOptions.add?.autoSave != null) options.dataTask!.addRow!.autoSave = dbOptions.add!.autoSave;
        if (dbOptions.add?.confirm != null) options.dataTask!.addRow!.confirm = dbOptions.add!.confirm;
    }
}

function setRemoveConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.remove) {
        if (dbOptions.remove?.autoSave != null) options.dataTask!.removeRow!.autoSave = dbOptions.remove!.autoSave;
        if (dbOptions.remove?.confirm != null) options.dataTask!.removeRow!.confirm = dbOptions.remove!.confirm;
    }
}

function setSaveConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.save) {
        if (dbOptions.save?.autoSaveChanges != null) options.dataTask!.saveData!.autoSave = dbOptions.save!.autoSaveChanges;
        if (dbOptions.save?.confirm != null) options.dataTask!.saveData!.confirm = dbOptions.save!.confirm;
        if (dbOptions.save?.debounceTime != null) options.dataTask!.saveData!.debounceTime = dbOptions.save!.debounceTime;
        if (dbOptions.save?.single != null) options.dataTask!.saveData!.single = dbOptions.save!.single;
        if (dbOptions.save?.refreshRowOnSave != null) options.dataTask!.saveData!.refreshRowOnSave = dbOptions.save!.refreshRowOnSave;
        if (dbOptions.save?.hideRefreshSpinner != null) options.dataTask!.saveData!.hideRefreshSpinner = dbOptions.save!.hideRefreshSpinner;
        if (dbOptions.save?.readAfterSave != null) options.dataTask!.saveData!.readAfterSave = dbOptions.save!.readAfterSave;
        if (dbOptions.save?.readAfterAdd != null) options.dataTask!.saveData!.readAfterAdd = dbOptions.save!.readAfterAdd;
        if (dbOptions.save?.onlySaveIsSelected != null) options.dataTask!.saveData!.onlySaveIsSelected = dbOptions.save!.onlySaveIsSelected;
        if (dbOptions.save?.saveInclLoadparms != null) options.dataTask!.saveData!.saveInclLoadparms = dbOptions.save!.saveInclLoadparms;
        if (dbOptions.save?.progressShowCancelButton != null) options.dataTask!.saveData!.progressShowCancelButton = dbOptions.save!.progressShowCancelButton;
        if (dbOptions.save?.postSaveDatatask != null) options.dataTask!.saveData!.postSaveDatatask = dbOptions.save!.postSaveDatatask;
        if (dbOptions.save?.postSaveDebounceTime != null) options.dataTask!.saveData!.postSaveDebounceTime = dbOptions.save!.postSaveDebounceTime;
        if (dbOptions.save?.postSaveReadAfter != null) options.dataTask!.saveData!.postSaveReadAfter = dbOptions.save!.postSaveReadAfter;
        if (dbOptions.save?.postSaveRunOnSaveError != null) options.dataTask!.saveData!.postSaveRunOnSaveError = dbOptions.save!.postSaveRunOnSaveError;
    }
}

function setGeneralConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.config) {
        if (dbOptions.config.pagination != null) options.kendo!.pageable = dbOptions.config.pagination;
        if (dbOptions.config.shortcuts != null) options.config!.shortcuts = dbOptions.config.shortcuts;
        if (dbOptions.config.groupDisplayType != null) options.config!.groupDisplayType = dbOptions.config.groupDisplayType;
        if (dbOptions.config.groupDefaultExpanded != null) options.config!.groupDefaultExpanded = dbOptions.config.groupDefaultExpanded;
        if (dbOptions.config.rowGroupingLock != null) options.config!.rowGroupingLock = dbOptions.config.rowGroupingLock;
        if (dbOptions.config.suppressGroupCount != null) options.config!.suppressGroupCount = dbOptions.config.suppressGroupCount;
        if (dbOptions.config.pivotMode != null) options.config!.pivotMode = dbOptions.config.pivotMode;
        if (dbOptions.config.enableStrictPivotColumnOrder != null) options.config!.enableStrictPivotColumnOrder = dbOptions.config.enableStrictPivotColumnOrder;
        if (dbOptions.config.allowRowDuplication != null) options.config!.allowRowDuplication = dbOptions.config.allowRowDuplication;
    }
}

function setCssConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.configCss) {
        if (dbOptions.configCss.compactness != null) options.config!.css!.compactness = dbOptions.configCss.compactness;
        if (dbOptions.configCss.fontsize != null) options.config!.css!.fontsize = dbOptions.configCss.fontsize;
        if (dbOptions.configCss.theme != null) options.config!.css!.theme = dbOptions.configCss.theme;
    }
}

function setNavigationConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.navigation) {
        if (dbOptions.navigation.altNav != null) options.config!.navigation!.altNav = dbOptions.navigation.altNav;
        if (dbOptions.navigation.newLine != null) options.config!.navigation!.newLine = dbOptions.navigation.newLine;
    }
}

function setTooltipConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.tooltip) {
        if (dbOptions.tooltip.trigger != null) options.config!.tooltip!.trigger = dbOptions.tooltip.trigger;
        if (dbOptions.tooltip.showDelay != null) options.config!.tooltip!.showDelay = dbOptions.tooltip.showDelay;
        if (dbOptions.tooltip.hideDelay != null) options.config!.tooltip!.hideDelay = dbOptions.tooltip.hideDelay;
    }
}

function setAggregatesFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (isAggregateConfigDefined(dbOptions.aggregates)) {
        if (dbOptions.aggregates instanceof Array) {
            if (!options.kendo?.aggregate || options.kendo.aggregate === true) {
                if (!options.kendo) options.kendo = {};

                options.kendo!.aggregate = [];
            }

            for (let aggregate of dbOptions.aggregates) {
                if (Array.isArray(options.kendo.aggregate)) {
                    options.kendo.aggregate.push(aggregate);
                }
            }
        } else {
            options.kendo!.aggregate = dbOptions.aggregates;
        }
    }
}

function setSelectableFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (dbOptions?.kendo?.selectable !== undefined && dbOptions?.kendo?.selectable !== null && (typeof dbOptions?.kendo?.selectable === 'boolean' || ['single', 'multiple', 'na'].includes(dbOptions.kendo.selectable)) && dbOptions.kendo.selectable !== 'na') {
        options.kendo!.selectable = dbOptions.kendo.selectable;
    }
}

function setSortableFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (dbOptions?.kendo?.sortable !== undefined && dbOptions?.kendo?.sortable !== null && (typeof dbOptions?.kendo?.sortable === 'boolean' || dbOptions.kendo.sortable !== 'na')) {
        options.kendo!.sortable = dbOptions.kendo.sortable;
    }
}

function setFilterableFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (dbOptions.kendo?.filterable !== undefined && dbOptions.kendo.filterable !== null && dbOptions.kendo.filterable !== 'na') options.kendo!.filterable = dbOptions.kendo.filterable !== 'row' ? dbOptions.kendo.filterable : { mode: 'row' };
}

function setSidebarConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (dbOptions.sidebar) Object.entries(dbOptions.sidebar).forEach(([key, value]) => value !== null && (options.config!.sidebar![<keyof GridSidebarConfig>key] = value));
}

function setExcelExportConfigFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (dbOptions.excelExportConfig) {
        if (dbOptions.excelExportConfig.useColnameForHeaders !== null) options!.excelExportConfig!.useColnameForHeaders = dbOptions.excelExportConfig.useColnameForHeaders;

        let excludedColumns = dbOptions.excelExportConfig.excludedColumns;
        let includedColumns = dbOptions.excelExportConfig.includedColumns;

        if (!!excludedColumns && Array.isArray(excludedColumns) && excludedColumns.length > 0 && excludedColumns.every((item) => typeof item === 'object' && Object.hasOwn(item, 'colname'))) {
            options!.excelExportConfig!.excludedColumns = excludedColumns.map((col) => col.colname);
        }

        if (!!includedColumns && Array.isArray(includedColumns) && includedColumns.length > 0 && includedColumns.every((item) => typeof item === 'object' && Object.hasOwn(item, 'colname'))) {
            options!.excelExportConfig!.includedColumns = includedColumns.map((col) => col.colname);
        }
    }
}

function setSpecialFuncFromDbOptions<TType extends GridRow = GridRow>(dbOptions: DBGridOptions, options: GridOptions<TType>) {
    if (!!dbOptions.specialFunc) options.config!.specialFunc!.cellDoubleClicks = dbOptions.specialFunc.cellDoubleClicks;

    if (!options.config?.specialFunc) {
        options.config!.specialFunc = {
            newTab: dbOptions.specialFunc?.newTab,
            gotoInOverflow: dbOptions.specialFunc?.gotoInOverflow,
            duplicate: dbOptions.specialFunc?.duplicate,
            buttons: [],
            groupButtons: [],
        };
    } else {
        options.config!.specialFunc!.newTab = dbOptions.specialFunc?.newTab;
        options.config!.specialFunc!.gotoInOverflow = dbOptions.specialFunc?.gotoInOverflow ?? options.config!.specialFunc!.gotoInOverflow;
        options.config!.specialFunc!.duplicate = dbOptions.specialFunc!.duplicate;
    }

    if (!options.config!.specialFunc!.buttons) {
        options.config!.specialFunc!.buttons = [];
    }

    if (!options.config!.specialFunc!.groupButtons) {
        options.config!.specialFunc!.groupButtons = [];
    }

    if (!!dbOptions.specialFunc?.buttons) {
        dbOptions.specialFunc.buttons.forEach((button) => {
            if (!!options.config?.specialFunc?.buttons && (options.config?.specialFunc?.buttons?.filter((specialButton) => specialButton.name === button.name).length ?? 0) === 0) {
                if (!!button.colgroup) {
                    options.config!.specialFunc!.groupButtons!.push({ ...button, field: button.colgroup });
                } else {
                    options.config!.specialFunc!.buttons!.push(button);
                }
            }
        });
    }
}
